import { type ReactNode, useRef, useState } from 'react';
import { Box, HStack, Icon } from '@chakra-ui/react';
import { GripVertical } from 'lucide-react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

// The draggable pill shell shared by the canvas' floating tools (the translation
// language bar and the find bar).
export function CanvasFloatingBar({
  children,
  position = 'absolute',
  barTestId,
  gripTestId,
}: {
  children: ReactNode;
  // `absolute` on the canvas; `static` on the unit guide page, where a sticky
  // wrapper owns the scroll-follow rather than this pill.
  position?: 'absolute' | 'static';
  barTestId?: string;
  gripTestId?: string;
}) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragStart = useRef<{
    pointerX: number;
    pointerY: number;
    baseX: number;
    baseY: number;
  } | null>(null);

  const onPointerDown = (event: React.PointerEvent) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };
  const onPointerMove = (event: React.PointerEvent) => {
    const start = dragStart.current;
    if (start === null) {
      return;
    }
    setOffset({
      x: start.baseX + (event.clientX - start.pointerX),
      y: start.baseY + (event.clientY - start.pointerY),
    });
  };
  const onPointerUp = (event: React.PointerEvent) => {
    event.currentTarget.releasePointerCapture(event.pointerId);
    dragStart.current = null;
  };

  // Absolute centres via left:50% + translateX(-50%); static centres in flow via
  // an auto margin on a content-width box (left:50% wouldn't work in flow).
  const isStatic = position === 'static';

  return (
    <Box
      position={position}
      top={isStatic ? undefined : 3}
      left={isStatic ? undefined : '50%'}
      mx={isStatic ? 'auto' : undefined}
      w={isStatic ? 'max-content' : undefined}
      maxW={isStatic ? '100%' : undefined}
      zIndex={isStatic ? undefined : 10}
      dir="ltr"
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      boxShadow="md"
      pl={1}
      pr={3}
      py={2}
      transform={
        isStatic
          ? `translate(${pxToRem(offset.x)}, ${pxToRem(offset.y)})`
          : `translate(calc(-50% + ${pxToRem(offset.x)}), ${pxToRem(offset.y)})`
      }
      data-testid={barTestId}
    >
      <HStack spacing={2}>
        {/* Deliberately aria-hidden: dragging is a pointer-only nicety, not required functionality. */}
        <Box
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
          color="fg.subtle"
          px={0.5}
          aria-hidden
          data-testid={gripTestId}
        >
          <Icon as={GripVertical} boxSize={iconSize.sm} />
        </Box>
        {children}
      </HStack>
    </Box>
  );
}
